import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, formatApiError } from "../../lib/api";
import { itemsLabel } from "../../lib/products";

const STATUS = { active: "Active", paused: "Paused", cancelled: "Cancelled" };
const tone = {
  active: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  paused: "text-amber-300 border-amber-400/40 bg-amber-400/10",
  cancelled: "text-rose-300 border-rose-400/40 bg-rose-400/10",
};

const Row = ({ s, onPatch }) => (
  <tr className="border-t border-white/10 align-top" data-testid={`admin-sub-row-${s.ref}`}>
    <td className="px-5 py-4 font-mono font-semibold text-[var(--gold)]">{s.ref}</td>
    <td className="px-5 py-4"><p className="font-semibold">{s.name}</p><p className="text-[var(--sage)]">{s.email}</p><p className="text-[var(--sage)]">{s.phone}</p></td>
    <td className="px-5 py-4"><p>{s.address}</p><p className="text-[var(--sage)]">{s.suburb}</p></td>
    <td className="px-5 py-4">
      <select value={s.frequency} disabled={s.status === "cancelled"} onChange={(e) => onPatch(s.id, { frequency: e.target.value })} className="rounded-full border border-white/15 bg-black/30 px-3 py-1.5 text-xs font-semibold text-white outline-none disabled:opacity-40" data-testid={`admin-sub-frequency-${s.ref}`}>
        <option value="weekly" className="bg-[#0F1410]">Weekly</option>
        <option value="fortnightly" className="bg-[#0F1410]">Fortnightly</option>
      </select>
      <p className="mt-1 text-[var(--sage)]">{itemsLabel(s).join(", ")}</p></td>
    <td className="px-5 py-4">
      <input type="date" value={s.next_order_date} disabled={s.status !== "active"} onChange={(e) => e.target.value && onPatch(s.id, { next_order_date: e.target.value })} className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5 text-xs text-white [color-scheme:dark] disabled:opacity-40" data-testid={`admin-sub-next-${s.ref}`} />
    </td>
    <td className="px-5 py-4">
      <select value={s.status} onChange={(e) => onPatch(s.id, { status: e.target.value })} className={`rounded-full border px-3 py-1.5 text-xs font-semibold outline-none ${tone[s.status]}`} data-testid={`admin-sub-status-${s.ref}`}>
        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k} className="bg-[#0F1410] text-white">{v}</option>)}
      </select>
    </td>
  </tr>
);

export const Subscriptions = () => {
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    http.get("/admin/subscriptions")
      .then((r) => setSubs(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const onPatch = async (id, body) => {
    try {
      const { data } = await http.patch(`/admin/subscriptions/${id}`, body);
      setSubs((ss) => ss.map((s) => (s.id === id ? data : s)));
      toast.success(`${data.ref} updated`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const active = subs.filter((s) => s.status === "active");
  return (
    <div className="mt-8" data-testid="admin-subscriptions">
      <p className="text-[var(--sage)]" data-testid="admin-subs-summary">
        {active.length} active · {active.reduce((a, s) => a + (s.quantity ?? 0), 0)} items per cycle. A new order and payment email is created automatically on each “Next order” date.
      </p>
      <div className="mt-6 overflow-x-auto rounded-3xl border border-white/10">
        <table className="w-full min-w-[960px] text-left text-sm">
          <thead className="bg-[#161D17] font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--sage)]">
            <tr>{["Ref", "Customer", "Address", "Plan", "Next order", "Status"].map((h) => <th key={h} className="px-5 py-4 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody>
            {subs.length === 0 && <tr><td colSpan={6} className="px-5 py-16 text-center text-[var(--sage)]" data-testid="admin-subs-empty">No subscriptions yet.</td></tr>}
            {subs.map((s) => <Row key={s.id} s={s} onPatch={onPatch} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};
